/**
 * CatalogPickerDialog (Story 32-3 — Epic 32 Multi-Account UX)
 *
 * The ADR-0004 catalog picker: when the agency already holds a multi-tenant
 * grant for a platform, Connect skips the OAuth dance and shows the grant's
 * resource catalog instead — per-resource checkboxes, a per-resource client
 * assignment Select (revealed when checked), a "Refresh available resources"
 * button (the on-demand 31-24 unit, forced), an opportunistic stale-grant
 * background refresh on open (AC #8, no force — the server's 6h guard keeps
 * it cheap), and the AC #12 escape hatch ("Connect a different account" →
 * the platform's normal OAuth flow).
 *
 * Design-directive compliance (§4.2 centered modal Dialog — NEVER a Sheet):
 * max-w-md, rounded-3xl, bg-black/35 overlay, 0.25s easeOut, motion stagger
 * (30ms increments, FLAT running index across grant groups), Phosphor icons
 * only, close blocked while the attach mutation is pending, reset-on-OPEN.
 *
 * Story 32-4 REMAP MODE (`remapTarget` prop): the ResourceUnavailableBanner
 * re-points an existing integration at another resource of the SAME platform.
 * Same chrome, different selection semantics — single-select radios, no
 * per-row client assignment, candidates filtered to `active` resources
 * excluding the currently-mapped one, confirm goes through PATCH
 * /api/integrations/:id/remap (useGrantCatalog.remapMutation) instead of the
 * attach POST. The "Connect a different account" escape hatch is hidden (the
 * empty state's hint covers re-connect/remove).
 *
 * Clients-list source: fetched internally via the shared `["clients"]` query
 * (the ClientDetailHeader pattern — same cache key, usually warm). Demo
 * clients are filtered out (the server rejects them anyway).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { ArrowsClockwise, PlugsConnected, Warning } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@provenleap/ui";
import { platformDisplayName } from "@provenleap/integrations";
import { apiGet } from "../../lib/apiClient";
import {
  CatalogRefreshCooldownError,
  LEGACY_RESOURCE_PREFIX,
  USABLE_TOKEN_STATUSES,
  isGrantStale,
  useGrantCatalog,
  type CatalogAttachmentInput,
  type GrantCatalogGrant,
} from "../../hooks/useGrantCatalog";
import type { ToastType } from "../../hooks/useToast";

// ============================================================================
// Types
// ============================================================================

interface PickerClient {
  id: string;
  name: string;
  demoMode?: boolean;
}

/** Story 32-4: the integration a remap-mode picker re-points. */
export interface CatalogPickerRemapTarget {
  integrationId: string;
  /** Currently-mapped resource — excluded from the candidate list. */
  currentResourceId: string | null;
  /** Display label for the dialog copy (account name or platform name). */
  label: string;
}

export interface CatalogPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Platform whose catalog to show (caller keeps it non-null via §4.6 lastValue). */
  platform: string;
  /** Client the connect flow was initiated from — each row's default assignment. */
  defaultClientId: string;
  showToast: (type: ToastType, message: string) => void;
  /** Fired after ≥1 attachment was created OR a remap succeeded (integration-list refetch). */
  onAttached?: () => void;
  /**
   * AC #12 escape hatch: close + start the platform's normal OAuth flow.
   * Optional since Story 32-4 — remap mode hides the affordance entirely.
   */
  onConnectNew?: () => void;
  /**
   * Story 32-4: non-null switches the dialog into REMAP mode (single-select,
   * platform-locked, PATCH remap on confirm). Caller keeps it non-null
   * through the exit animation (§4.6 lastValue).
   */
  remapTarget?: CatalogPickerRemapTarget | null;
}

// ============================================================================
// Component
// ============================================================================

export function CatalogPickerDialog({
  open,
  onOpenChange,
  platform,
  defaultClientId,
  showToast,
  onAttached,
  onConnectNew,
  remapTarget,
}: CatalogPickerDialogProps) {
  const isRemap = !!remapTarget;
  const { catalogQuery, refreshMutation, attachMutation, remapMutation } =
    useGrantCatalog({
      platform,
      enabled: open,
      onIntegrationChange: onAttached,
    });

  // Tenant clients for the per-row assignment Select (shared cache key).
  const { data: clients } = useQuery<PickerClient[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) throw new Error("Failed to load clients");
      const json = (await res.json()) as { data: PickerClient[] };
      return json.data;
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const assignableClients = useMemo(
    () => (clients ?? []).filter((c) => !c.demoMode),
    [clients]
  );

  // Review R1-F2: `defaultClientId` may not be assignable (demo client page,
  // stale id) — fall back to the first assignable client so the revealed
  // Select never holds an invalid value the server would reject.
  const effectiveDefaultClientId = useMemo(() => {
    if (assignableClients.some((c) => c.id === defaultClientId)) {
      return defaultClientId;
    }
    return assignableClients[0]?.id ?? "";
  }, [assignableClients, defaultClientId]);

  // Selection: resourceId → assigned clientId (checked rows only).
  const [selection, setSelection] = useState<Map<string, string>>(new Map());
  // Story 32-4 remap mode: the single chosen target resource (radio).
  const [remapChoice, setRemapChoice] = useState<string | null>(null);
  // Failed rows from the last partial-success attach: "resourceId:clientId" → errorCode.
  const [failedRows, setFailedRows] = useState<Map<string, string>>(new Map());
  // Grants with an in-flight refresh (skeleton state per group).
  const [refreshingTokens, setRefreshingTokens] = useState<Set<string>>(new Set());
  // One-shot AC #8 opportunistic refresh per dialog-open.
  const autoRefreshDone = useRef(false);

  // Exit-window guard (32-2 review R2 precedent): AnimatePresence freezes
  // the EXITING subtree with its last-seen props, so a click on "Attach" or
  // "Connect a different account" during the ~250ms exit animation of a
  // CANCELLED dialog would fire a STALE closure (launching OAuth / mutating
  // data the user just dismissed). A ref pierces the frozen closure — it
  // always reads the CURRENT open state.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Reset on OPEN, never on close (§4.6 — no mid-exit flash).
  useEffect(() => {
    if (open) {
      setSelection(new Map());
      setRemapChoice(null);
      setFailedRows(new Map());
      autoRefreshDone.current = false;
    }
  }, [open]);

  // §4.2 — block close while a confirm mutation is pending.
  function handleOpenChange(next: boolean): void {
    if (!next && (attachMutation.isPending || remapMutation.isPending)) return;
    onOpenChange(next);
  }

  // Defensive `__legacy_` display filter — the server already excludes
  // synthetic rows from the GET; this guarantees a placeholder can never
  // render even if a future endpoint change regresses that.
  //
  // Story 32-4 remap mode narrows further (AC #6): candidates are the
  // tenant's ACTIVE resources of this platform, excluding the integration's
  // currently-mapped resource; grant groups left empty by the filter are
  // dropped (a single global empty state replaces per-group ones).
  const rawGrants = useMemo(
    () =>
      (catalogQuery.data?.grants ?? []).map((grant) => ({
        ...grant,
        resources: grant.resources.filter(
          (r) => !r.externalAccountId.startsWith(LEGACY_RESOURCE_PREFIX)
        ),
      })),
    [catalogQuery.data]
  );
  const grants = useMemo(() => {
    if (!remapTarget) return rawGrants;
    return rawGrants
      .map((grant) => ({
        ...grant,
        resources: grant.resources.filter(
          (r) =>
            r.resourceStatus === "active" &&
            r.resourceId !== remapTarget.currentResourceId
        ),
      }))
      .filter((grant) => grant.resources.length > 0);
  }, [rawGrants, remapTarget]);

  /** Sequential per-grant refresh (AC #5). force=true → manual button. */
  async function runRefresh(targets: GrantCatalogGrant[], force: boolean) {
    if (targets.length === 0) return;
    setRefreshingTokens((prev) => {
      const next = new Set(prev);
      for (const grant of targets) next.add(grant.oauthTokenId);
      return next;
    });
    let warnedFailures = false;
    let warnedUnhealthy = false;
    for (const grant of targets) {
      try {
        const summary = await refreshMutation.mutateAsync({
          oauthTokenId: grant.oauthTokenId,
          force,
        });
        // 31-24 review note: "discovered" can carry per-platform failures —
        // surface them as a soft warning, never as a hard error.
        if (summary.failures.length > 0 && !warnedFailures) {
          warnedFailures = true;
          showToast("info", "Some platforms could not be refreshed.");
        }
        if (summary.status === "skipped_unhealthy" && force && !warnedUnhealthy) {
          warnedUnhealthy = true;
          showToast("info", "This account needs to be reconnected first.");
        }
      } catch (err) {
        if (err instanceof CatalogRefreshCooldownError && force) {
          showToast(
            "info",
            "This catalog was refreshed moments ago — try again in a minute."
          );
        }
        // Other refresh errors are non-fatal: the existing list stays.
      } finally {
        setRefreshingTokens((prev) => {
          const next = new Set(prev);
          next.delete(grant.oauthTokenId);
          return next;
        });
      }
    }
  }

  // AC #8 — opportunistic background refresh of stale grants on open
  // (WITHOUT force: the server's built-in 6h guard makes this idempotent).
  useEffect(() => {
    if (!open || autoRefreshDone.current || !catalogQuery.data) return;
    autoRefreshDone.current = true;
    const stale = catalogQuery.data.grants.filter(
      (grant) =>
        USABLE_TOKEN_STATUSES.has(grant.tokenStatus) && isGrantStale(grant)
    );
    void runRefresh(stale, false);
  }, [open, catalogQuery.data]);

  const anyRefreshing = refreshingTokens.size > 0;

  // ── Selection helpers ──────────────────────────────────────────────────────

  // Review R4-F1: `||` (not `??`) — a row checked BEFORE the clients query
  // resolved snapshots "" as its assignment; resolving empty values through
  // effectiveDefaultClientId at READ time heals those rows the moment the
  // clients list arrives (otherwise they'd look checked but silently never
  // join the attach payload).
  function rowClientId(resourceId: string): string {
    return selection.get(resourceId) || effectiveDefaultClientId;
  }

  function toggleResource(resourceId: string): void {
    setSelection((prev) => {
      const next = new Map(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.set(resourceId, effectiveDefaultClientId);
      return next;
    });
  }

  function assignClient(resourceId: string, clientId: string): void {
    setSelection((prev) => {
      const next = new Map(prev);
      next.set(resourceId, clientId);
      return next;
    });
  }

  // Attachable = checked rows whose (resource, selected client) pair is not
  // already attached (AC #4 "Already connected" combinations never count).
  const attachments: CatalogAttachmentInput[] = useMemo(() => {
    const out: CatalogAttachmentInput[] = [];
    for (const grant of grants) {
      for (const resource of grant.resources) {
        if (!selection.has(resource.resourceId)) continue; // unchecked
        // Review R4-F1: heal ""-snapshot assignments (row checked before the
        // clients query resolved) through the effective default at read time.
        const clientId =
          selection.get(resource.resourceId) || effectiveDefaultClientId;
        if (!clientId) continue; // no assignable client exists at all
        if (resource.resourceStatus !== "active") continue;
        if (resource.attachedClients.some((a) => a.clientId === clientId)) continue;
        out.push({ resourceId: resource.resourceId, clientId });
      }
    }
    return out;
  }, [grants, selection, effectiveDefaultClientId]);

  function handleAttach(): void {
    if (!openRef.current) return; // exit-window guard
    if (attachments.length === 0) return;
    attachMutation.mutate(attachments, {
      onSuccess: (data) => {
        if (data.failed.length === 0) {
          const parts: string[] = [];
          if (data.created.length > 0) parts.push(`${data.created.length} attached`);
          if (data.existing.length > 0) {
            parts.push(`${data.existing.length} already connected`);
          }
          showToast("success", parts.join(", ") || "Nothing to attach");
          onOpenChange(false);
        } else {
          // Partial failure: keep the dialog open with failed rows marked.
          setFailedRows(
            new Map(
              data.failed.map((f) => [`${f.resourceId}:${f.clientId}`, f.errorCode])
            )
          );
          const okCount = data.created.length + data.existing.length;
          showToast(
            "error",
            `${data.failed.length} of ${okCount + data.failed.length} attachments failed.`
          );
        }
      },
      onError: (err) => {
        showToast(
          "error",
          err instanceof Error ? err.message : "Failed to attach accounts"
        );
      },
    });
  }

  /** Story 32-4: confirm in remap mode — PATCH instead of attach POST. */
  function handleRemap(): void {
    if (!openRef.current) return; // exit-window guard
    if (!remapTarget || !remapChoice) return;
    remapMutation.mutate(
      {
        integrationId: remapTarget.integrationId,
        oauthGrantResourceId: remapChoice,
      },
      {
        onSuccess: () => {
          showToast("success", "Integration remapped — syncing from the new account.");
          onOpenChange(false);
        },
        onError: (err) => {
          // RFC 9457 `detail` travels in err.message (useGrantCatalog).
          showToast(
            "error",
            err instanceof Error ? err.message : "Failed to remap integration"
          );
        },
      }
    );
  }

  function handleConnectNew(): void {
    if (!openRef.current) return; // exit-window guard
    if (attachMutation.isPending || !onConnectNew) return;
    onOpenChange(false);
    onConnectNew();
  }

  const displayName = platformDisplayName(platform);
  const attachCount = attachments.length;
  const distinctClients = new Set(attachments.map((a) => a.clientId)).size;
  const remapCandidateCount = isRemap
    ? grants.reduce((n, g) => n + g.resources.length, 0)
    : 0;

  // Flat running index across groups for the 30ms stagger (Dev Notes).
  let staggerIndex = 0;
  const nextDelay = () => 0.03 * (++staggerIndex);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid="catalog-picker-dialog"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                      <PlugsConnected size={19} className="text-primary shrink-0" />
                      {isRemap
                        ? `Remap to another ${displayName} account`
                        : `Choose ${displayName} accounts`}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      {isRemap
                        ? `${remapTarget!.label} is no longer accessible. Choose the ${displayName} account this integration should use instead — no re-login needed.`
                        : `Your agency already authorized ${displayName}. Pick the accounts to attach — no re-login needed.`}
                    </DialogPrimitive.Description>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    aria-label="Refresh available resources"
                    data-testid="catalog-picker-refresh"
                    className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    disabled={anyRefreshing || rawGrants.length === 0}
                    onClick={() => void runRefresh(rawGrants, true)}
                  >
                    <ArrowsClockwise
                      size={16}
                      className={anyRefreshing ? "animate-spin" : undefined}
                    />
                  </Button>
                </div>

                <div className="mt-6 space-y-5">
                  {catalogQuery.isPending && (
                    <SkeletonRows count={3} delay={nextDelay()} />
                  )}

                  {!catalogQuery.isPending && !isRemap && grants.length === 0 && (
                    <motion.p
                      className="text-sm text-muted-foreground"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      No {displayName} authorizations found. Connect an account
                      below to get started.
                    </motion.p>
                  )}

                  {/* Story 32-4 AC #6: remap-mode empty state — the header's
                      "Refresh available resources" button stays live (it
                      targets the RAW grant list, not the filtered one). */}
                  {!catalogQuery.isPending &&
                    isRemap &&
                    remapCandidateCount === 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: nextDelay() }}
                        data-testid="catalog-picker-remap-empty"
                      >
                        <p className="text-sm text-muted-foreground">
                          No other {displayName} resources available.
                        </p>
                        <p className="mt-1 text-[12px] text-muted-foreground">
                          Try the refresh button above, connect {displayName}{" "}
                          with a different account, or remove the integration
                          instead.
                        </p>
                      </motion.div>
                    )}

                  {grants.map((grant) => {
                    const grantLabel =
                      grant.providerAccountName ??
                      grant.providerAccountId ??
                      "Account";
                    const usable = USABLE_TOKEN_STATUSES.has(grant.tokenStatus);
                    const isRefreshing = refreshingTokens.has(grant.oauthTokenId);
                    return (
                      <div
                        key={grant.oauthTokenId}
                        data-testid={`catalog-grant-${grant.oauthTokenId}`}
                      >
                        <motion.div
                          className="flex items-center gap-2 mb-2"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: nextDelay() }}
                        >
                          <span className="text-[13px] font-semibold text-foreground truncate">
                            {grantLabel}
                          </span>
                          <TokenStatusPill status={grant.tokenStatus} />
                        </motion.div>

                        {!usable && (
                          <motion.p
                            className="text-[12px] text-muted-foreground mb-2 flex items-center gap-1"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: nextDelay() }}
                          >
                            <Warning size={14} className="text-destructive shrink-0" />
                            This account needs to be reconnected first.
                          </motion.p>
                        )}

                        {isRefreshing ? (
                          <SkeletonRows count={Math.max(grant.resources.length, 2)} delay={nextDelay()} />
                        ) : grant.resources.length === 0 ? (
                          <motion.p
                            className="text-[13px] text-muted-foreground"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: nextDelay() }}
                          >
                            No {displayName} accounts found for this
                            authorization. Try Refresh, or connect a different
                            account below.
                          </motion.p>
                        ) : (
                          <div className="space-y-1.5">
                            {grant.resources.map((resource) => {
                              // Story 32-4 remap mode: single-select radio,
                              // no client assignment. Rows already attached
                              // to THIS client are disabled (the server
                              // would 409 "Already attached").
                              if (isRemap) {
                                const attachedToClient =
                                  resource.attachedClients.some(
                                    (a) => a.clientId === defaultClientId
                                  );
                                return (
                                  <motion.div
                                    key={resource.resourceId}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: nextDelay() }}
                                    className={`rounded-2xl px-3 py-2 ${attachedToClient ? "opacity-60" : ""}`}
                                    style={{ border: "0.5px solid var(--hairline)" }}
                                    data-testid={`catalog-resource-${resource.resourceId}`}
                                  >
                                    <label
                                      className={`flex items-center gap-2.5 ${attachedToClient ? "cursor-not-allowed" : "cursor-pointer"}`}
                                    >
                                      <input
                                        type="radio"
                                        name="catalog-remap-choice"
                                        className="h-4 w-4 shrink-0 accent-primary cursor-pointer disabled:cursor-not-allowed"
                                        checked={remapChoice === resource.resourceId}
                                        disabled={attachedToClient}
                                        onChange={() =>
                                          setRemapChoice(resource.resourceId)
                                        }
                                      />
                                      <span className="min-w-0 flex-1">
                                        <span className="block text-[13px] text-foreground truncate">
                                          {resource.externalAccountName ??
                                            resource.externalAccountId}
                                        </span>
                                        <span className="block text-[11px] text-muted-foreground truncate">
                                          {resource.externalAccountId}
                                        </span>
                                      </span>
                                      {attachedToClient && (
                                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                                          Already connected
                                        </span>
                                      )}
                                    </label>
                                  </motion.div>
                                );
                              }

                              const checked = selection.has(resource.resourceId);
                              const currentClient = rowClientId(resource.resourceId);
                              const unavailable =
                                resource.resourceStatus !== "active";
                              const attachedToCurrent =
                                resource.attachedClients.some(
                                  (a) => a.clientId === currentClient
                                );
                              const failedCode = failedRows.get(
                                `${resource.resourceId}:${currentClient}`
                              );
                              return (
                                <motion.div
                                  key={resource.resourceId}
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: nextDelay() }}
                                  className={`rounded-2xl px-3 py-2 ${unavailable ? "opacity-60" : ""}`}
                                  style={{ border: "0.5px solid var(--hairline)" }}
                                  data-testid={`catalog-resource-${resource.resourceId}`}
                                >
                                  <label
                                    className={`flex items-center gap-2.5 ${unavailable ? "cursor-not-allowed" : "cursor-pointer"}`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 shrink-0 accent-primary cursor-pointer disabled:cursor-not-allowed"
                                      checked={checked}
                                      disabled={unavailable}
                                      onChange={() =>
                                        toggleResource(resource.resourceId)
                                      }
                                    />
                                    <span className="min-w-0 flex-1">
                                      <span className="block text-[13px] text-foreground truncate">
                                        {resource.externalAccountName ??
                                          resource.externalAccountId}
                                      </span>
                                      <span className="block text-[11px] text-muted-foreground truncate">
                                        {resource.externalAccountId}
                                      </span>
                                    </span>
                                    {unavailable && (
                                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                                        Unavailable
                                      </span>
                                    )}
                                    {!unavailable && attachedToCurrent && (
                                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                                        Already connected
                                      </span>
                                    )}
                                  </label>

                                  <AnimatePresence initial={false}>
                                    {checked && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.2, ease: "easeInOut" }}
                                        className="overflow-hidden"
                                      >
                                        <div className="pt-2 pl-6.5">
                                          <Select
                                            value={currentClient}
                                            onValueChange={(value) =>
                                              assignClient(
                                                resource.resourceId,
                                                value
                                              )
                                            }
                                          >
                                            <SelectTrigger
                                              className="h-9 w-full rounded-2xl text-[13px] shadow-xs"
                                              style={{
                                                border: "0.5px solid var(--hairline)",
                                              }}
                                              aria-label="Assign to client"
                                            >
                                              <SelectValue placeholder="Choose a client" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {assignableClients.map((client) => (
                                                <SelectItem
                                                  key={client.id}
                                                  value={client.id}
                                                  className="text-[13px]"
                                                >
                                                  {client.name}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          {attachedToCurrent && (
                                            <p className="mt-1 text-[11px] text-muted-foreground">
                                              Already connected to this client —
                                              pick another to attach it
                                              elsewhere.
                                            </p>
                                          )}
                                          {failedCode && (
                                            <p className="mt-1 text-[11px] text-destructive">
                                              Could not attach ({failedCode}).
                                            </p>
                                          )}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </motion.div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Footer */}
                  <motion.div
                    className={`flex items-center gap-3 pt-1 ${isRemap ? "justify-end" : "justify-between"}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: nextDelay() }}
                  >
                    {isRemap ? (
                      <Button
                        type="button"
                        variant="brand"
                        data-testid="catalog-picker-remap"
                        className="rounded-2xl text-[13px] cursor-pointer"
                        onClick={handleRemap}
                        disabled={!remapChoice || remapMutation.isPending}
                      >
                        {remapMutation.isPending ? "Remapping…" : "Remap account"}
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="catalog-picker-connect-new"
                          className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all text-[13px]"
                          style={{ border: "0.5px solid var(--hairline)" }}
                          onClick={handleConnectNew}
                          disabled={attachMutation.isPending}
                        >
                          Connect a different account
                        </Button>
                        <Button
                          type="button"
                          variant="brand"
                          data-testid="catalog-picker-attach"
                          className="rounded-2xl text-[13px] cursor-pointer"
                          onClick={handleAttach}
                          disabled={attachCount === 0 || attachMutation.isPending}
                        >
                          {attachMutation.isPending
                            ? "Attaching…"
                            : `Attach ${attachCount} to ${distinctClients <= 1 ? "client" : "clients"}`}
                        </Button>
                      </>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

// ============================================================================
// Bits
// ============================================================================

/** Pulse-skeleton resource rows (refresh / initial-load state). */
function SkeletonRows({ count, delay }: { count: number; delay: number }) {
  return (
    <motion.div
      className="space-y-1.5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay }}
      data-testid="catalog-picker-skeleton"
    >
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="h-12 rounded-2xl bg-muted animate-pulse"
          style={{ border: "0.5px solid var(--hairline)" }}
        />
      ))}
    </motion.div>
  );
}

/** Minimal token-status pill (32-5 builds the full banner system). */
function TokenStatusPill({ status }: { status: string }) {
  const usable = USABLE_TOKEN_STATUSES.has(status);
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ${
        usable
          ? "bg-emerald-50 text-emerald-700"
          : "bg-destructive/10 text-destructive"
      }`}
    >
      {usable ? (status === "expiring_soon" ? "Expiring soon" : "Connected") : "Needs reconnect"}
    </span>
  );
}
